import React, { useState, useRef } from 'react';
import { avatarService } from '../../services/avatarService';
import { useToast } from './ToastProvider';
import { imageCacheService } from '../../services/imageCacheService';

interface AvatarUploadProps {
  userId: string;
  currentAvatarUrl?: string;
  onAvatarUpdated?: (newAvatarUrl: string | null) => void;
  onAvatarRemoved?: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  userId,
  currentAvatarUrl,
  onAvatarUpdated,
  onAvatarRemoved,
  size = 'md',
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(currentAvatarUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();

  // Atualizar avatarUrl quando currentAvatarUrl mudar
  React.useEffect(() => {
    setAvatarUrl(currentAvatarUrl);
  }, [currentAvatarUrl]);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar arquivo
    const validation = avatarService.validateImageFile(file);
    if (!validation.isValid) {
      addToast(validation.error!, 'error');
      return;
    }

    setIsUploading(true);
    try {
      await avatarService.uploadAvatar(userId, file);
      
      // Limpar cache do avatar para forçar recarregamento
      imageCacheService.removeCachedImage(userId);
      
      // Obter nova URL do avatar (sem cache para garantir que é a mais recente)
      const newAvatarUrl = await avatarService.getAvatarUrl(userId, false);
      if (newAvatarUrl) {
        setAvatarUrl(newAvatarUrl);
        onAvatarUpdated?.(newAvatarUrl);
        addToast('Avatar atualizado com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao fazer upload do avatar:', error);
      addToast('Erro ao atualizar avatar. Tente novamente.', 'error');
    } finally {
      setIsUploading(false);
      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que o clique abra o seletor de arquivos
    
    if (!avatarUrl) return;
    
    setIsRemoving(true);
    try {
      await avatarService.removeAvatar(userId);
      
      // Limpar cache do avatar após remoção
      imageCacheService.removeCachedImage(userId);
      
      setAvatarUrl(undefined);
      onAvatarUpdated?.(null);
      onAvatarRemoved?.();
      addToast('Avatar removido com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao remover avatar:', error);
      addToast('Erro ao remover avatar. Tente novamente.', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`${className} flex flex-col items-center space-y-2`}>
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden cursor-pointer border-2 border-gray-300 hover:border-blue-500 transition-colors duration-200 flex items-center justify-center bg-gray-100 group relative`}
        onClick={handleClick}
      >
        {isUploading || isRemoving ? (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        ) : avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Avatar"
            className="w-full h-full object-cover"
            onError={() => setAvatarUrl(undefined)}
          />
        ) : (
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        )}
        
        {/* Overlay de upload - sempre mostrar ícone de câmera */}
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all duration-200 flex items-center justify-center"
        >
          <svg
            className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </div>
      </div>
      
      {/* Link de remoção - só aparece quando há avatar */}
      {avatarUrl && (
        <button
          onClick={handleRemoveAvatar}
          disabled={isRemoving}
          className="text-xs text-red-600 hover:text-red-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isRemoving ? 'Removendo...' : 'Remover'}
        </button>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

